/**
 * CSRF-token-hantering för Lyktan (S-12-11c).
 *
 * Backend sätter en spegel-cookie `harden_admin_csrf` (icke-HttpOnly,
 * SameSite=Strict) vid lyckad MFA-verifiering. Värdet är HMAC av
 * access-token-jti på serversidan. Klienten:
 *   1. läser cookien via `document.cookie`,
 *   2. speglar värdet i `X-CSRF-Token`-headern på alla state-changing
 *      requests (POST/PUT/PATCH/DELETE) mot backend.
 *
 * Hard-rules som tillämpas:
 *   - Inget cookie-värde lagras i sessionStorage/localStorage. Vi läser
 *     det från document.cookie precis när vi behöver det och kastar
 *     värdet efter requesten är skickad.
 *   - Inga API-anrop till backend görs utan CSRF-header om endpointen är
 *     mutating. Om cookien saknas (ej inloggad / cookien expirerad)
 *     returnerar [csrfHeader] tomt objekt; backend svarar då 403 vilket
 *     UI:t mappar till "Sessionen har gått ut".
 *
 * **Varför inte injicera token via en fetch-wrapper för alla calls?** Det
 * vore strikt taget en bekvämare API. Men explicit `csrfHeader()`-anrop
 * gör det tydligt i diff:ar exakt vilka endpoints som är CSRF-skyddade,
 * och eliminerar risken att en buggig wrapper skulle släppa igenom en
 * mutation utan token.
 */

/** Cookie-namn som matchar backendens [AuthConfig.adminCsrfCookieName]. */
export const CSRF_COOKIE_NAME = 'harden_admin_csrf';

/** Header-namn som backendens [DEFAULT_CSRF_HEADER_NAME] förväntar sig. */
export const CSRF_HEADER_NAME = 'X-CSRF-Token';

/**
 * Läser CSRF-cookien från document.cookie. Returnerar `null` om cookien
 * inte finns (utloggad, expirerad eller annat dokument).
 *
 * Implementationen tar inte beroenden — fungerar både i browser-miljö
 * och i vitest med jsdom. Returnerar null om `document` saknas (SSR-läge,
 * ovanligt i Lyktan men säkrast).
 */
export function readCsrfToken(
  cookieSource: string = typeof document !== 'undefined' ? document.cookie : '',
): string | null {
  if (!cookieSource) return null;
  // document.cookie är en sträng på formen "name1=value1; name2=value2".
  // Vi splittar och letar efter cookie-namnet exakt — undviker substring-
  // matchning som skulle kunna luras av t.ex. "harden_admin_csrf_other".
  const parts = cookieSource.split(';');
  for (const part of parts) {
    const trimmed = part.trim();
    const eqIndex = trimmed.indexOf('=');
    if (eqIndex < 0) continue;
    const name = trimmed.substring(0, eqIndex);
    if (name === CSRF_COOKIE_NAME) {
      const value = trimmed.substring(eqIndex + 1);
      // Tom sträng räknas som "ingen token" — hellre fail-fast än att
      // skicka tom header som backend skulle avvisa.
      return value.length > 0 ? value : null;
    }
  }
  return null;
}

/**
 * Bygger ett header-objekt med CSRF-headern om token finns. Returnerar
 * tomt objekt om cookien saknas — då slår mutationen mot backendens
 * 403-svar och UI mappar till "Sessionen har gått ut".
 *
 * Användning:
 *   ```ts
 *   await fetch('/api/admin/hearths', {
 *     method: 'POST',
 *     credentials: 'include',
 *     headers: { 'Content-Type': 'application/json', ...csrfHeader() },
 *     body: JSON.stringify(payload),
 *   });
 *   ```
 */
export function csrfHeader(
  cookieSource: string = typeof document !== 'undefined' ? document.cookie : '',
): Record<string, string> {
  const token = readCsrfToken(cookieSource);
  if (token === null) return {};
  return { [CSRF_HEADER_NAME]: token };
}
